import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LanguageIcon from '@mui/icons-material/Language';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import SpotlightCard from '../Common/SpotlightCard';
import api from '../../services/api';

const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMovie();
  }, [id]);

  const fetchMovie = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/movies/${id}`);
      setMovie(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch movie details');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          pt: 12,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !movie) {
    return (
      <Container maxWidth="lg" sx={{ pt: 12 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Movie not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          variant="outlined"
          sx={{
            color: '#6366f1',
            borderColor: '#6366f1',
            '&:hover': {
              borderColor: '#8b5cf6',
              background: 'rgba(99, 102, 241, 0.1)',
            },
          }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: 12, pb: 8, background: '#0f172a' }}>
      <Container maxWidth="xl">
        {/* Back Button - Styled like Navbar Buttons */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{
            mb: 3,
            color: 'white',
            borderColor: 'rgba(99, 102, 241, 0.5)',
            background: 'rgba(99, 102, 241, 0.1)',
            fontWeight: 600,
            px: 3,
            py: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#6366f1',
              background: 'rgba(99, 102, 241, 0.2)',
              transform: 'translateX(-4px)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            },
          }}
        >
          Back
        </Button>

        {/* Main Content Card */}
        <SpotlightCard
          className="spotlight-card"
          spotlightColor="rgba(99, 102, 241, 0.2)"
          style={{
            padding: '0',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left Side - Poster (Fixed Width) */}
            <Box
              sx={{
                width: { xs: '100%', md: '350px' },
                minWidth: { md: '350px' },
                height: { xs: '450px', md: 'auto' },
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={movie.posterUrl || 'https://via.placeholder.com/350x500/1e293b/6366f1?text=No+Poster'}
                alt={movie.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/350x500/1e293b/6366f1?text=No+Poster';
                }}
              />
              {/* Rating Badge on Poster */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(0, 0, 0, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                }}
              >
                <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                <Typography variant="h6" fontWeight={700} color="white">
                  {movie.rating}/10
                </Typography>
              </Box>
            </Box>

            {/* Right Side - Movie Info (Flexible Width) */}
            <Box sx={{ flex: 1, p: { xs: 3, md: 4 }, minWidth: 0 }}>
              {/* Title */}
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #a1a1aa 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  wordBreak: 'break-word',
                }}
              >
                {movie.title}
              </Typography>

              {/* Genres */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {movie.genre?.map((genre, index) => (
                  <Chip
                    key={index}
                    label={genre}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      fontWeight: 600,
                      border: '1px solid rgba(99, 102, 241, 0.5)',
                    }}
                  />
                ))}
              </Box>

              {/* Quick Info */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={700} color="white">
                    {movie.rating}/10
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body1" color="text.secondary">
                    {movie.duration} minutes
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body1" color="text.secondary">
                    {new Date(movie.releaseDate).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="white"
                  sx={{ mb: 1.5 }}
                >
                  Overview
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  {movie.description}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

              {/* Director */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="white"
                  sx={{ mb: 1.5 }}
                >
                  Director
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: '#6366f1' }} />
                  <Typography variant="body1" color="text.secondary">
                    {movie.director}
                  </Typography>
                </Box>
              </Box>

              {/* Cast */}
              {movie.cast && movie.cast.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="white"
                    sx={{ mb: 1.5 }}
                  >
                    Cast
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {movie.cast.map((actor, index) => (
                      <Chip
                        key={index}
                        label={actor}
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(99, 102, 241, 0.5)',
                          color: 'white',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

              {/* Additional Info Grid */}
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LanguageIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600} color="white">
                      Language
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {movie.language}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PublicIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600} color="white">
                      Country
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {movie.country}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <AccessTimeIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600} color="white">
                      Runtime
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {movie.duration} min
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </SpotlightCard>
      </Container>
    </Box>
  );
};

export default MovieDetail;
